import {
  AnimationCallbackEvent,
  ApplicationRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  Signal,
  input,
  linkedSignal,
  signal,
  viewChild,
  afterNextRender,
  afterRenderEffect,
  untracked,
} from '@angular/core';
import { Dialog, DialogRef } from '@angular/cdk/dialog';

import { DatePipe, NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { NavigationStart, Router } from '@angular/router';
import {
  MibBadgeDirective,
  MibBareButtonDirective,
  MibBreakpointService,
  MibCase,
  MibCaseCardComponent,
  MibDurationPipe,
  MibIconComponent,
  MibMenuComponent,
  MibMenuEntry,
  MibMenuTriggerDirective,
  MibPalette,
  MibButtonDirective,
  MibTableCellDirective,
  MibTableCellWidgetDirective,
  MibTableComponent,
  MibTableColumn,
  MibTableSection,
  MibTableSectionEndDirective,
  watchSize,
} from '@medinbox/angular';
import { CaseStatsComponent } from '../case-stats/case-stats.component';
import { provideCaseVocabulary } from '../case-categories';
import { CASE_SORTS, CaseLibraryStore } from '../case-library-store';
import { CaseSessionsService } from '@features/case-details/case-sessions.service';
import { CASE_CATEGORIES, caseCategoryLabels } from '../case-categories';
import { AvailabilityStatus } from '@shared/enums/availability-status.enum';
import { VideoProcessingStatus } from '@shared/enums/video-processing-status.enum';
import { CaseDisplay } from '@shared/enums/case-library';
import { CardsState, CaseRow, Video } from '@shared/models';
import { CaseLibraryService } from '../case-library.service';
import {
  ConfirmDialogData,
  MibConfirmDialogComponent,
  MibSimpleEditDialogComponent,
  SimpleEditDialogData,
  MibToastService,
  MibSpinnerComponent,
} from '@medinbox/angular';
import { SharingService } from '@features/sharing/sharing.service';
import { VideoHubService } from '@core/services/video-hub.service';
import { OpenCasesService } from '@core/services';
import { filter, Subject, takeUntil } from 'rxjs';
import { ScrollPositionStore } from '@shared/directives/scroll-position';
import { PermissionService } from '@core/permissions';
import { PublishService } from '@features/publish';
import { colsOf, computeLayout, parkOf, withLoadingDelay } from '@shared/utils';
import { CARDS_MOTION } from '@shared/constants';
import { ChunksMergeRequestStatus } from '@shared/enums/chunks-merge-request-status';
import { PublicationStatus } from '@shared/enums/publication-status.enum';
import { CardsMotion } from './cards-motion/cards-motion';

/* One object for every favourite row, so the band's end can be told apart by identity. */
const FAVOURITES_SECTION: MibTableSection = { label: 'Favorites' };

const FAVOURITES_TOGGLES = { collapsed: 'All favorites', expanded: 'Show less' } as const;

/* The aside's `w-62` in library-zones/library-zones.ts. */
export const FILTERS_ASIDE_WIDTH = 248;

/* The table scrolls inside itself, so its position is kept apart from the cards' one. */
const LIST_SCROLL_KEY = 'case-library-list';

/** `title` is the column that gives way, clipping with an ellipsis. */
const CASE_COLUMNS: MibTableColumn[] = [
  { key: 'title', label: 'Case', size: '100%', overflow: 'ellipsis' },
  { key: 'categories', label: 'Specialty' },
  { key: 'createdAt', label: 'Date' },
  /* `start` against the default: a duration is always the same width, so ranging it end drifts. */
  { key: 'durationSeconds', label: 'Time', type: 'number', align: 'start' },
  /* `fit-content`: without it the column takes a share of the surplus and leaves the edge. */
  { key: 'actions', align: 'center', size: 'fit-content' },
];

function caseRowOf(video: Video, section: MibTableSection | null = null): CaseRow {
  return {
    href: `/video-details/${video.id}`,
    title: video.title,
    thumbnail: video.thumbnailUrl,
    categories: caseCategoryLabels(video.categories),
    createdAt: video.createdAt,
    durationSeconds: video.durationSeconds,
    video,
    section,
  };
}

/** Written on the picture, because it is the picture that is not there yet. */
const PREPARING = 'The livestream is still in progress. AI analysis and chat follow once it ends.';

/**
 * The whole of what casechat can tell the card: no operator or procedure exists here.
 * `href` is a real address, not a router path, so a ctrl-click opens a tab.
 */
export function caseOfVideo(video: Video, mayPublish: boolean, mayShare: boolean): MibCase {
  const merged = video.chunksMergeRequestStatus === ChunksMergeRequestStatus.Merged;

  return {
    title: video.title,
    href: `/video-details/${video.id}`,
    thumbnail: video.thumbnailUrl,
    duration: video.durationSeconds,
    categories: caseCategoryLabels(video.categories),
    favourite: video.isFavourite === true,
    // From the session: the merge status stays `waiting` until the first live merge.
    live: video.isLive === true,
    flags: flagsOf(video, mayPublish),
    ...(mayShare ? { share: {} } : {}),
    ...(merged ? {} : { preparing: PREPARING }),
  };
}

/** What the case has to announce, and nothing that goes without saying. */
function flagsOf(video: Video, mayPublish: boolean): string[] {
  const published = video.publicationStatus === PublicationStatus.Published;
  const flags: string[] = [];

  if (published) {
    flags.push('published');
  }

  // Health information warns publishers only, and only while the case is not yet published.
  if (video.stats.piiCount > 0 && mayPublish && !published) {
    flags.push('phi');
  }
  if (video.mediaStatus === AvailabilityStatus.Error) {
    flags.push('mediaFailed');
  }
  if (video.chatStatus === AvailabilityStatus.Error) {
    flags.push('chatFailed');
  }

  return flags;
}

/** The columns the CSS grid lays now, unlike `colsOf`, which computes them from a width. */
export function renderedColumnsOf(grid: HTMLElement): number {
  return getComputedStyle(grid).gridTemplateColumns.split(' ').length;
}

/** How many rows fit under the table's first two section bands; Infinity until a row is drawn. */
function visibleRowsOf(table: HTMLElement, rows: HTMLElement): number {
  const [band, next] = Array.from(rows.querySelectorAll<HTMLElement>('[data-mib-section]'));
  const rowHeight = band?.nextElementSibling?.getBoundingClientRect().height;
  if (!band || !rowHeight) return Infinity;
  const bands = band.getBoundingClientRect().height + (next?.getBoundingClientRect().height ?? 0);
  const height = table.getBoundingClientRect().bottom - rows.getBoundingClientRect().top - bands;
  return Math.max(1, Math.floor(height / rowHeight));
}

@Component({
  selector: 'mai-case-list',
  imports: [
    CaseStatsComponent,
    DatePipe,
    MibBadgeDirective,
    MibBareButtonDirective,
    MibButtonDirective,
    MibCaseCardComponent,
    MibDurationPipe,
    MibIconComponent,
    MibMenuComponent,
    MibMenuTriggerDirective,
    MibSpinnerComponent,
    MibTableCellDirective,
    MibTableCellWidgetDirective,
    MibTableComponent,
    MibTableSectionEndDirective,
    NgOptimizedImage,
    NgTemplateOutlet,
  ],
  providers: [provideCaseVocabulary(), CardsMotion],
  templateUrl: './case-list.html',
  /* Without a height here, every percentage height underneath resolves against nothing. */
  host: { class: 'block h-full' },
})
export class CaseList implements OnInit, OnDestroy {
  private readonly caseLibraryService = inject(CaseLibraryService);
  private readonly sharingService = inject(SharingService);
  private readonly publishService = inject(PublishService);
  private readonly dialog = inject(Dialog);
  private readonly videoHub = inject(VideoHubService);
  private readonly destroy$ = new Subject<void>();
  protected readonly permissions = inject(PermissionService);
  private readonly toastService = inject(MibToastService);
  private readonly router = inject(Router);
  private readonly sessions = inject(CaseSessionsService);
  private readonly openCases = inject(OpenCasesService);
  private readonly scrollPositions = inject(ScrollPositionStore);
  private readonly injector = inject(Injector);
  private readonly breakpointService = inject(MibBreakpointService);
  private readonly motion = inject(CardsMotion);
  private readonly appRef = inject(ApplicationRef);
  private readonly notExpanded = computed(() => this.breakpointService.sizeClass() !== 'expanded');
  private readonly host: HTMLElement = inject(ElementRef).nativeElement;
  private readonly table = viewChild(MibTableComponent);
  private readonly tableElement = viewChild(MibTableComponent, { read: ElementRef });
  /* Infinity until measured, as the grid's columns below. */
  private readonly listRows = signal(Infinity);
  private readonly favouritesGrid = viewChild<ElementRef<HTMLElement>>('favouritesGrid');
  /* Infinity until measured: without a `ResizeObserver`, every favourite shows and no tile. */
  private readonly gridColumns = signal(Infinity);
  private filtersOpeningColumns = Infinity;
  private readonly recentSection = viewChild<ElementRef<HTMLElement>>('recentSection');
  private readonly cardsRoot = viewChild<ElementRef<HTMLElement>>('cardsRoot');
  /** A sort the reader chose, until its answer is drawn. */
  private reordering = false;
  private travelPending = false;
  private pushing = false;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private openDialogRef?: DialogRef<any>;
  private isDeleting = false;
  /* The cards of the first row at common widths: their pictures are fetched first, the others wait. */
  protected readonly FIRST_ROW = 6;
  /* The favorites that draw a picture come first; Angular warns past 10 priority pictures. */
  protected readonly recentPriorityCount = computed(
    () =>
      this.FIRST_ROW -
      this.favourites()
        .slice(0, this.FIRST_ROW)
        .filter((video) => video.thumbnailUrl).length,
  );
  protected VideoProcessingStatus = VideoProcessingStatus;
  protected ChunksMergeRequestStatus = ChunksMergeRequestStatus;
  protected readonly showSpinner: Signal<boolean> = withLoadingDelay(() =>
    this.library.resource().isLoading(),
  );

  /** The categories the reader kept. Empty is the whole library, which is what the chips call `all`. */
  readonly categories = input<readonly string[]>([]);

  /** Pictures or columns. The cases are the same either way; only their reading changes. */
  readonly display = input<CaseDisplay>(CaseDisplay.Grid);

  /** The desktop filters aside, which takes its width from the cases' column as it opens. */
  readonly filtersOpen = input(false);

  /** The chat, or the phone's filters, beside the cases: a phone then shows half the cases' box. */
  readonly besideChat = input(false);

  protected readonly library = inject(CaseLibraryStore);
  protected readonly CaseDisplay = CaseDisplay;
  protected readonly loaded = computed(() => this.library.cases() !== undefined);
  /**
   * How the cards are grouped: the whole library leads with the favorites, the favorites chosen are
   * the whole answer whatever the other filters, and any other search is one grid.
   */
  protected readonly grouping = computed<'strips' | 'favourites' | 'grid'>(() => {
    if (this.library.favouritesOnly()) {
      return 'favourites';
    }
    return this.library.filtered() ? 'grid' : 'strips';
  });
  protected readonly markedFavourites = computed(() =>
    this.videos().filter((video) => video.isFavourite),
  );
  /** The strips' cards are placed by `layout` and slide with the zones. */
  protected readonly placesCards = computed(
    () => this.notExpanded() && this.display() === CaseDisplay.Grid && this.grouping() !== 'grid',
  );
  private readonly reducedMotion = this.breakpointService.prefersReducedMotion;
  private readonly cardsState = signal<CardsState>({
    boxWidth: 0,
    chatOpen: false,
    favOpen: false,
    park: 'ride',
    lastMove: 'chat',
    snap: false,
    trim: false,
  });
  protected readonly layout = computed(() => {
    const state = this.cardsState();
    const strips = this.grouping() === 'strips';
    return computeLayout({
      ...state,
      favOpen: state.favOpen || !strips,
      trim: state.trim && strips,
      snap: state.snap || this.reducedMotion(),
      nFav: this.markedFavourites().length,
      nRecent: strips ? this.videos().length : 0,
    });
  });
  /** Half the box on a phone beside the chat: the cases' other content keeps to what shows. */
  protected readonly phoneWidth = computed(() => {
    if (!this.notExpanded()) {
      return null;
    }
    return this.cardsState().boxWidth > 0 ? this.layout().visW : null;
  });
  /* Always the folded arrow: `rotate-180` turns it once the favourites are open. */
  protected readonly favouritesToggle = computed(() => ({
    label: FAVOURITES_TOGGLES[this.library.favouritesExpanded() ? 'expanded' : 'collapsed'],
    icon: this.notExpanded() ? ('arrow-down' as const) : ('arrow-right' as const),
  }));
  /** One row cannot hold every favourite, or they are all open: the toggle shows either way. */
  protected readonly showsFavouritesToggle = computed(() => {
    const fits = this.display() === CaseDisplay.List ? this.listRows() : this.gridColumns();
    return (
      this.grouping() === 'strips' &&
      (this.library.favouritesExpanded() || this.markedFavourites().length > fits)
    );
  });
  protected readonly favourites = computed(() => {
    const marked = this.markedFavourites();
    if (this.grouping() === 'favourites' || this.library.favouritesExpanded()) {
      return marked;
    }
    if (this.display() === CaseDisplay.List) {
      return marked.slice(0, this.listRows());
    }
    return marked.slice(0, this.firstRowFavourites());
  });
  /** The favourites of the folded row: beside the toggle's tile, except on a phone where it wraps. */
  private readonly firstRowFavourites = computed(() => {
    const tileInRow = this.showsFavouritesToggle() && !this.notExpanded();
    return Math.max(1, this.gridColumns() - Number(tileInRow));
  });
  /** Whether the toggle chose the favourites on show: only then do their cards fade in and out. */
  protected readonly favouritesFromToggle = linkedSignal({
    source: () => [this.library.cases(), this.gridColumns()],
    computation: () => false,
  });
  /** Whether a column change chose the favourites on show: a wider row lets cards in, a narrower one drops them. */
  protected readonly favouritesFromResize = linkedSignal({
    source: () => [this.library.cases(), this.grouping()],
    computation: () => false,
  });
  protected readonly favouriteEnter = computed(() => {
    if (this.favouritesFromToggle()) {
      return 'favourite-revealing';
    }
    return this.favouritesFromResize() ? 'favourite-arriving' : '';
  });
  /** The cards fill whole rows, so the grid's tile wraps onto a row of its own, as always on a phone. */
  protected readonly favouritesToggleAlone = computed(
    () => this.notExpanded() || this.favourites().length % this.gridColumns() === 0,
  );
  /** The list's favourites band ends on the toggle. */
  protected readonly favouritesToggleBand = computed(() =>
    this.showsFavouritesToggle() ? FAVOURITES_SECTION : null,
  );
  /* `1fr` so the cards follow the chat's collapse; `auto-fill` because the column's width is what counts. */
  protected readonly gridClasses =
    'relative grid grid-cols-[repeat(auto-fill,minmax(232px,1fr))] gap-4';
  protected readonly titleTextClasses = 'text-mib-branding-900 py-2 text-xl font-semibold';
  /* Sticky inside its own section: the next section's title takes its place as the reader scrolls. */
  protected readonly stripTitleClasses = `${this.titleTextClasses} sticky top-0 z-20 bg-white`;
  protected readonly sortTitle = computed(() => CASE_SORTS[this.library.sortOrder()].title);
  protected readonly failed = computed(() => !this.loaded() && !!this.library.resource().error());
  public videos = computed(() => this.library.cases() || []);

  constructor() {
    effect(() => this.library.categories.set(this.categories()));
    effect(() => {
      const open = this.filtersOpen();
      untracked(() => this.followFilters(open));
    });
    // Parking the hidden favourite holds the new state back a frame.
    effect(() => {
      const open = this.besideChat();
      untracked(() => this.followChat(open));
    });
    effect(() => {
      const open = this.library.favouritesExpanded();
      untracked(() => this.followFavourites(open));
    });
    // A view effect runs before its template is refreshed: the old cards are still there to shoot.
    let shown: Video[] | undefined;
    effect(() => {
      const resource = this.library.resource();
      const settled = !resource.isLoading();
      // A failed resource throws on read.
      const cases = resource.error() ? shown : this.library.cases();
      untracked(() => {
        const changed = cases !== shown;
        shown = cases;
        this.followReorder(changed, settled);
      });
    });
    inject(DestroyRef).onDestroy(watchSize(this.host, () => this.measureBox()));
    afterRenderEffect((onCleanup) => {
      const grid = this.favouritesGrid()?.nativeElement;
      if (grid) {
        onCleanup(watchSize(grid, () => this.measureColumns(grid)));
      }
    });
    afterRenderEffect((onCleanup) => {
      const table: HTMLElement | undefined = this.tableElement()?.nativeElement;
      const rows = table?.querySelector<HTMLElement>('[role="rowgroup"]');
      if (!table || !rows) {
        return;
      }
      const measure = () => this.listRows.set(visibleRowsOf(table, rows));
      onCleanup(watchSize(table, measure));
      onCleanup(watchSize(rows, measure));
    });
    afterNextRender(() =>
      this.table()?.scrollToPosition(this.scrollPositions.get(LIST_SCROLL_KEY)),
    );
  }

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        const table = this.table();
        if (table) {
          this.scrollPositions.set(LIST_SCROLL_KEY, table.scrollPosition());
        }
      });
    this.videoHub.onVideoDeleted$.pipe(takeUntil(this.destroy$)).subscribe((video) => {
      if (this.caseLibraryService.wasCanceledHere(video.id)) {
        this.toastService.show({
          message: `Upload of "${video.title}" canceled.`,
          type: 'info',
          duration: 5000,
        });
      } else {
        this.toastService.show({
          message: this.isDeleting
            ? 'Case deletion successful'
            : `Case "${video.title}" has been deleted.`,
          type: this.isDeleting ? 'success' : 'warning',
          duration: this.isDeleting ? undefined : 5000,
        });
      }
      if (this.openDialogRef) {
        this.openDialogRef.close();
      }
      this.isDeleting = false;
    });
  }

  protected readonly columns = CASE_COLUMNS;

  /** The same cases as rows, under the same section titles as the cards. */
  protected readonly rows = computed<CaseRow[]>(() => {
    const layout = this.grouping();
    if (layout === 'grid') {
      return this.videos().map((video) => caseRowOf(video));
    }
    const favourites = this.favourites().map((video) => caseRowOf(video, FAVOURITES_SECTION));
    if (layout === 'favourites') {
      return favourites;
    }
    const sorted = { label: this.sortTitle() };
    return [...favourites, ...this.videos().map((video) => caseRowOf(video, sorted))];
  });
  protected readonly sectionOf = (row: CaseRow): MibTableSection | null => row.section;
  /* A favourite is drawn twice, once in each section: the key tells the two rows apart. */
  protected readonly rowKeyOf = (row: CaseRow): string =>
    `${row.section?.label ?? ''}:${row.video.id}`;

  /** The tile and the cases below glide to their new place; folding may leave the button out of view. */
  protected toggleFavourites(button: HTMLElement): void {
    if (this.placesCards()) {
      this.togglePlacedFavourites();
      return;
    }
    const folding = this.library.favouritesExpanded();
    const followers =
      this.display() === CaseDisplay.Grid
        ? this.motion.followersOf(button, this.recentSection()?.nativeElement)
        : [];
    const before = this.motion.placesOf(followers);
    const buttonTop = button.getBoundingClientRect().top;
    this.favouritesFromToggle.set(true);
    this.library.favouritesExpanded.set(!folding);
    afterNextRender(
      () => {
        if (folding) {
          const grid =
            this.display() === CaseDisplay.Grid ? this.favouritesGrid()?.nativeElement : undefined;
          this.motion.keepToggleInView(button, buttonTop, grid);
        }
        this.motion.slide(followers, before, this.besideChat());
      },
      { injector: this.injector },
    );
  }

  /** Expanded cards travel; a phone places its own, and the list and reduced motion change at once. */
  private travels(): boolean {
    return !this.notExpanded() && this.display() === CaseDisplay.Grid && !this.reducedMotion();
  }

  /** The chat or the filters pushing the cases: `apply` gives them their final width, each card travels to its new place. */
  followPush(apply: () => void): void {
    const root = this.cardsRoot()?.nativeElement;
    if (!root || !this.travels()) {
      apply();
      return;
    }
    const travel = this.motion.shoot(root);
    this.pushing = true;
    apply();
    // Drawn now, the favourites strip refilled for its new columns before any observer: one travel.
    this.appRef.tick();
    this.fitFavouritesRow();
    this.appRef.tick();
    this.pushing = false;
    // The favourites the row drops leave the DOM only after Angular's leave runs: measured before, they wrap to a second row.
    this.motion.onNextFrame(() => travel('push', true));
  }

  /** Not a resize to the favourites: they trail in and out with the travel, not fade. */
  private fitFavouritesRow(): void {
    const grid = this.favouritesGrid()?.nativeElement;
    if (!grid) {
      return;
    }
    this.favouritesFromResize.set(false);
    this.gridColumns.set(renderedColumnsOf(grid));
  }

  /** Called before a sort the reader chose: the cards travel to it, then to its answer. */
  travelToNextLists(): void {
    if (!this.travels()) {
      return;
    }
    this.reordering = true;
    this.travelAfterRender();
  }

  /* A failed sort keeps the same cases: left waiting, a later unrelated change would travel. */
  private followReorder(changed: boolean, settled: boolean): void {
    if (!this.reordering) {
      return;
    }
    if (!this.travels()) {
      this.reordering = false;
      return;
    }
    if (changed) {
      this.travelAfterRender();
      return;
    }
    if (settled) {
      this.reordering = false;
    }
  }

  /** Taken before the cases change; one shot per render, so a removed card leaves once. */
  private travelAfterRender(): void {
    const root = this.cardsRoot()?.nativeElement;
    if (!root || this.travelPending) {
      return;
    }
    this.travelPending = true;
    const strips = this.grouping() === 'strips';
    const travel = this.motion.shoot(root);
    afterNextRender(
      () => {
        this.travelPending = false;
        travel('reorder', strips && this.grouping() === 'strips');
        this.reordering = this.library.resource().isLoading();
      },
      { injector: this.injector },
    );
  }

  private togglePlacedFavourites(): void {
    if (this.cardsState().snap) {
      return;
    }
    this.library.favouritesExpanded.update((open) => !open);
  }

  /**
   * Opening beside the chat, the hidden favourite first moves from its row to its folded slot.
   * Folding many, the rows between the first and the last two go at once, the scroll with them.
   */
  private followFavourites(open: boolean): void {
    const state = this.cardsState();
    if (state.favOpen === open) {
      return;
    }
    if (open && (state.trim || (state.chatOpen && state.park !== 'fold'))) {
      this.openFromParked(state);
      return;
    }
    const { trimH, allFavY } = this.layout();
    if (!open && !state.trim && trimH > 0 && this.placesCards()) {
      this.foldTrimmed(trimH);
      return;
    }
    this.moveInPlace(open, allFavY);
  }

  private openFromParked(state: CardsState): void {
    this.snapThen({ park: parkOf(state), trim: false }, { favOpen: true, lastMove: 'favourites' });
  }

  private foldTrimmed(height: number): void {
    this.snapThen({ trim: true }, {}, () => this.dropTrimmedRows(height));
  }

  /** `tileY` is the toggle's place before the move, which a fold keeps on screen. */
  private moveInPlace(open: boolean, tileY: number | null): void {
    this.cardsState.update((s) => ({
      ...s,
      favOpen: open,
      lastMove: 'favourites',
      park: parkOf(s),
    }));
    if (!open && this.placesCards()) {
      // Once drawn: a fold committed from a frame callback renders later, and the scroll would lead it.
      afterNextRender(() => this.keepTileInPlace(tileY), { injector: this.injector });
    }
  }

  /** Folding, the box scrolls up on the tile's curve as far as the tile rises, so it stays where it was clicked. */
  private keepTileInPlace(before: number | null): void {
    const after = this.layout().allFavY;
    this.motion.scrollUp(before === null || after === null ? 0 : before - after);
  }

  /** The rows just dropped leave the screen as they were: the box scrolls up as much, then the fold runs. */
  private dropTrimmedRows(height: number): void {
    this.motion.scrollUpAtOnce(height);
    this.motion.onNextFrame(() => this.moveInPlace(false, this.layout().allFavY));
  }

  /** Closing the chat, a folded hidden favourite first moves back onto its row, to trail the push. */
  private followChat(open: boolean): void {
    const state = this.cardsState();
    if (state.chatOpen === open) {
      return;
    }
    if (!open && !state.favOpen && state.park === 'fold') {
      this.snapThen({ park: 'ride' }, { chatOpen: false, lastMove: 'chat' });
      return;
    }
    this.cardsState.update((s) => ({ ...s, chatOpen: open, lastMove: 'chat', park: 'ride' }));
  }

  /** The first width places the cards at once: from no width at all, they would slide in. */
  private measureBox(): void {
    const boxWidth = this.host.getBoundingClientRect().width;
    const before = this.cardsState().boxWidth;
    if (boxWidth === before) {
      return;
    }
    if (before === 0) {
      this.snapThen({ boxWidth }, {});
      return;
    }
    this.cardsState.update((s) => ({ ...s, boxWidth }));
  }

  /** Applies `prep` without transitions, `snapped` in the same frame, then `next` with them, a frame later. */
  private snapThen(
    prep: Partial<CardsState>,
    next: Partial<CardsState>,
    snapped?: () => void,
  ): void {
    this.cardsState.update((s) => ({ ...s, ...prep, snap: true }));
    afterNextRender(
      {
        read: () => {
          // Reading a size makes the browser apply the parked places the next state moves from.
          void this.host.offsetHeight;
          snapped?.();
          this.motion.onNextFrame(() =>
            this.cardsState.update((s) => ({ ...s, ...next, snap: false })),
          );
        },
      },
      { injector: this.injector },
    );
  }

  /** The extra favourites rise in order as the toggle opens them. */
  protected riseDelay(index: number): string | null {
    const extra = index - this.firstRowFavourites();
    return extra < 0 ? null : `${CARDS_MOTION.RISE_DELAY + extra * CARDS_MOTION.RISE_STAGGER}ms`;
  }

  /* Bound as an event: a leave class is never applied to a card drawn through the template outlet. */
  protected cardLeave(event: AnimationCallbackEvent, index: number): void {
    const leave = this.reducedMotion()
      ? undefined
      : this.leaveOf(event.target as HTMLElement, index);
    if (!leave) {
      event.animationComplete();
      return;
    }
    leave.onfinish = leave.oncancel = () => event.animationComplete();
  }

  /** Only a favourite the toggle folds or a narrower row drops animates; an unmarked one goes at once. */
  private leaveOf(card: HTMLElement, index: number): Animation | undefined {
    const extra = index - this.firstRowFavourites();
    const folded = this.favouritesFromToggle();
    const dropped = this.favouritesFromResize() && this.filtersOpen();
    if (
      extra < 0 ||
      this.library.favouritesExpanded() ||
      this.grouping() !== 'strips' ||
      this.display() !== CaseDisplay.Grid ||
      (!folded && !dropped)
    ) {
      return undefined;
    }
    const firstRow = this.firstRowFavourites();
    this.motion.takeLeavingOutOfFlow(card.parentElement ?? card, firstRow);
    if (folded) {
      return this.motion.foldOut(card, extra, this.markedFavourites().length - firstRow - 1);
    }
    return this.motion.fadeOut(card);
  }

  /** The first measure is not a resize: the favourites it leaves out were never seen. */
  private measureColumns(grid: HTMLElement): void {
    const columns = renderedColumnsOf(grid);
    // The aside is still sliding open: the favourites already dropped to where it ends.
    if (columns > this.filtersOpeningColumns) {
      return;
    }
    this.filtersOpeningColumns = Infinity;
    const before = this.gridColumns();
    if (Number.isFinite(before) && columns !== before) {
      this.favouritesFromResize.set(true);
    }
    this.gridColumns.set(columns);
  }

  /** The aside narrows the grid over 640ms: the favourites drop at the click to what it will hold. */
  private followFilters(open: boolean): void {
    const grid = this.favouritesGrid()?.nativeElement;
    this.filtersOpeningColumns = Infinity;
    // A push already gave the cases their final width: `followPush` refills the strip itself.
    if (!open || !grid || this.pushing || !Number.isFinite(this.gridColumns())) {
      return;
    }
    const columns = colsOf(grid.getBoundingClientRect().width - FILTERS_ASIDE_WIDTH);
    this.filtersOpeningColumns = columns;
    if (columns >= this.gridColumns()) {
      return;
    }
    this.favouritesFromResize.set(true);
    this.gridColumns.set(columns);
  }

  /** The categories a row could not show, as a menu opened from its count. */
  protected restItems(categories: readonly string[]): MibMenuEntry[] {
    return categories.slice(1).map((category) => ({ label: category, value: category }));
  }

  protected restLabel(categories: readonly string[]): string {
    return `${categories.length - 1} more: ${categories.slice(1).join(', ')}`;
  }

  /** The ramp a category wears, so a badge in the list matches the one on a card. */
  protected paletteOf(label: string): MibPalette {
    const found = Object.values(CASE_CATEGORIES).find((category) => category.label === label);

    return found?.palette ?? 'grey';
  }

  /** One case as the card reads it. */
  protected caseOf(video: Video): MibCase {
    return caseOfVideo(video, this.permissions.canPublishCase(), this.permissions.canShareCase());
  }

  /** The card is a real link: left to the browser, a click would reload the application. */
  protected open(video: Video, event: MouseEvent): void {
    event.preventDefault();
    // What the card knows names the tab and draws its bar at once, before the case has loaded.
    this.sessions.open(video.id).foresee(video);
    void this.router.navigate(['/video-details', video.id]);
  }

  /** A ctrl-click keeps the case in the bar, where the browser would open another window for it. */
  protected openAside(video: Video, event: MouseEvent): void {
    event.preventDefault();
    // A tab already held loads its case when it is read, not before.
    if (!this.openCases.opened().some((held) => held.id === video.id)) {
      this.sessions.open(video.id).foresee(video);
    }
  }

  /** What a reader may do to this case, and nothing they may not. Nothing at all means no disc. */
  protected menuOf(video: Video): MibMenuEntry[] | null {
    const items: MibMenuEntry[] = [];

    if (this.permissions.canEditCase()) {
      items.push({ label: 'Edit', value: 'edit', icon: 'pencil' });
    }
    if (this.permissions.canShareCase()) {
      items.push({ label: 'Share', value: 'share', icon: 'share-stroke' });
    }
    if (this.permissions.canPublishCase() && video.publicationStatus !== undefined) {
      items.push(
        this.isPublished(video)
          ? { label: 'Stop publishing', value: 'publish', icon: 'user-lock' }
          : { label: 'Publish', value: 'publish', icon: 'user-check' },
      );
    }
    if (this.permissions.canDeleteCase()) {
      items.push(
        { separator: true },
        { label: 'Delete', value: 'delete', icon: 'trash', theme: 'danger' },
      );
    }

    return items.length > 0 ? items : null;
  }

  protected act(action: string, video: Video): void {
    switch (action) {
      case 'edit':
        this.onEdit(video);
        break;
      case 'share':
        this.onShare(video);
        break;
      case 'publish':
        this.onPublish(video);
        break;
      case 'delete':
        this.onDelete(video);
        break;
    }
  }

  protected isPublished(video: Video): boolean {
    return video.publicationStatus === PublicationStatus.Published;
  }

  onEdit(video: Video) {
    this.openDialogRef = this.openEditPopup(video);
  }

  onShare(video: Video) {
    void this.sharingService.shareVideo(video).then((ref) => {
      this.openDialogRef = ref;
      ref.closed.subscribe(() => (this.openDialogRef = undefined));
    });
  }

  onPublish(video: Video) {
    this.publishService.togglePublish(video);
  }

  onDelete(video: Video) {
    this.openDialogRef = this.openConfirmationDeletePopup(video);
    this.isDeleting = true;
  }

  openEditPopup(video: Video): DialogRef<string> {
    const dialogRef = this.dialog.open<string, SimpleEditDialogData>(MibSimpleEditDialogComponent, {
      data: {
        dialogTitle: 'Edit Case Name',
        fieldLabel: 'Case name',
        currentValue: video.title,
        fieldRows: 3,
      },
    });
    dialogRef.closed.subscribe((newTitle) => {
      this.openDialogRef = undefined;
      if (newTitle !== undefined && newTitle !== null) {
        this.caseLibraryService.edit(video.id, { title: newTitle }).subscribe({
          next: () => this.library.reload(),
          error: (error) => console.error('Error updating video:', error),
        });
      }
    });
    return dialogRef;
  }

  openConfirmationDeletePopup(video: Video): DialogRef<boolean> {
    const dialogRef = this.dialog.open<boolean, ConfirmDialogData>(MibConfirmDialogComponent, {
      data: {
        description: 'Are you sure you want to permanently delete this case?',
        title: 'Delete this case?',
        confirmLabel: 'Delete',
        theme: 'danger',
      },
    });
    dialogRef.closed.subscribe((result) => {
      this.openDialogRef = undefined;
      if (result) {
        this.caseLibraryService.delete(video.id).subscribe({
          error: (error) => {
            console.error('Error deleting video:', error);
            this.isDeleting = false;
          },
        });
      }
    });

    return dialogRef;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
